import type { Business } from "@/lib/types";
import { router } from "expo-router";
import React, { useCallback, useMemo, useState } from "react";
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from "react-native";
import { SafeAreaView, useSafeAreaInsets } from "react-native-safe-area-context";

import DetailResultsNavigator from "@/components/directory/DetailResultsNavigator";
import PublicMenu from "@/components/public/PublicMenu";
import SeoHead from "@/components/seo/SeoHead";
import {
  getDirectoryResultsSession,
  updateDirectoryResultsSelectedIndex,
} from "@/lib/directory/resultsSession";
import { buildBusinessProfileSeo } from "@/lib/seo";

import BusinessCardResponsive from "./BusinessCardResponsive";
import DesktopSidebar from "./DesktopSidebar";
import FeaturedCarouselSmall from "./FeaturedCarouselSmall";
import FeaturedVertical from "./FeaturedVertical";

type Props = {
  business: Business;
  currentSlug: string;
  renderSeo?: boolean;
};

const EMPTY_SLUGS: string[] = [];

export default function BusinessScreen({
  business,
  currentSlug,
  renderSeo = true,
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false);

  const insets = useSafeAreaInsets();
  const { width, height } = useWindowDimensions();

  const isDesktop = width >= 1024;
  const showTopNavigator = isDesktop;
  const showBottomNavigator = !isDesktop;

  const session = getDirectoryResultsSession();

  const resultSlugs = useMemo<string[]>(
    () => session?.slugs ?? EMPTY_SLUGS,
    [session?.slugs]
  );

  const currentIndex = useMemo(
    () => resultSlugs.indexOf(currentSlug),
    [resultSlugs, currentSlug]
  );

  const safeIndex = currentIndex >= 0 ? currentIndex : 0;
  const total = resultSlugs.length;

  const safeBusiness = useMemo(
    () =>
      ({
        logoUrl: "",
        name: "",
        slug: "",
        phone: "",
        rating: 0,
        reviewCount: 0,
        isOpenNow: false,
        closesAt: "",
        category: "",
        subCategory: [],
        addressLine: "",
        cityLine: "",
        shortDescription: "",
        mapUrl: "",
        website: "",
        facebook: "",
        instagram: "",
        twitter: "",
        whatsappCtaType: "chat",
        whatsappChatNumber: "",
        whatsappCatalogUrl: "",
        itemsTitle: "Our Products",
        items: [],
        ...(business || {}),
      } as Business),
    [business]
  );

  const seo = useMemo(
    () => (renderSeo ? buildBusinessProfileSeo(safeBusiness, currentSlug) : null),
    [safeBusiness, currentSlug, renderSeo]
  );

  const profileReturnTo = useMemo(
    () => (currentSlug ? `/p/${currentSlug}` : ""),
    [currentSlug]
  );

  const closeMenu = useCallback(() => {
    setMenuOpen(false);
  }, []);

  const handleBack = useCallback(() => {
    router.back();
  }, []);

  const handleResults = useCallback(() => {
    router.back();
  }, []);

  const goToSlug = useCallback((nextSlug: string, nextIndex: number) => {
    updateDirectoryResultsSelectedIndex(nextIndex);

    router.replace({
      pathname: "/p/[slug]",
      params: { slug: nextSlug },
    });
  }, []);

  const handlePrev = useCallback(() => {
    if (safeIndex <= 0) return;

    const nextSlug = resultSlugs[safeIndex - 1];
    if (!nextSlug) return;

    goToSlug(nextSlug, safeIndex - 1);
  }, [goToSlug, resultSlugs, safeIndex]);

  const handleNext = useCallback(() => {
    if (safeIndex >= total - 1) return;

    const nextSlug = resultSlugs[safeIndex + 1];
    if (!nextSlug) return;

    goToSlug(nextSlug, safeIndex + 1);
  }, [goToSlug, resultSlugs, safeIndex, total]);

  const desktopContentHeight = Math.max(620, height - insets.top - 96);

  const desktopPage = (
    <View
      style={[
        styles.page,
        styles.pageDesktop,
        { minHeight: desktopContentHeight, height: desktopContentHeight },
      ]}
    >
      <View style={styles.colLeft}>
        <DesktopSidebar business={safeBusiness} />
      </View>

      <View style={styles.colCenter}>
        <BusinessCardResponsive business={safeBusiness} />
      </View>

      <View style={styles.colRight}>
        <View style={styles.colRightFrame}>
          <FeaturedVertical
            excludeBusinessId={
              (safeBusiness as any).id ||
              (safeBusiness as any).businessId ||
              null
            }
          />
        </View>
      </View>
    </View>
  );

  const mobilePage = (
    <ScrollView
      key={currentSlug}
      style={styles.scroll}
      contentContainerStyle={[
        styles.scrollContent,
        showBottomNavigator && total > 0 && { paddingBottom: 94 + insets.bottom },
      ]}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.page}>
        <View style={styles.fullWidth}>
          <BusinessCardResponsive business={safeBusiness} />
        </View>

        <View style={styles.mobileFeaturedWrap}>
          <View style={styles.featuredHeader}>
            <Text style={styles.featuredTitle}>⭐ Featured Businesses</Text>
            <Text style={styles.featuredHint}>Swipe →</Text>
          </View>

          <FeaturedCarouselSmall />
        </View>
      </View>
    </ScrollView>
  );

  return (
    <SafeAreaView style={styles.screen}>
      {seo ? <SeoHead {...seo} /> : null}

      <View style={[styles.topbar, { paddingTop: Math.max(8, insets.top - 18) }]}>
        <View style={styles.topbarSide}>
          <Pressable onPress={handleBack} style={styles.topBtn}>
            <Text style={styles.topBtnText}>← Back</Text>
          </Pressable>
        </View>

        <View style={styles.topbarCenter}>
          {showTopNavigator && total > 0 ? (
            <DetailResultsNavigator
              mode="top"
              currentIndex={safeIndex}
              total={total}
              onPrev={handlePrev}
              onResults={handleResults}
              onNext={handleNext}
            />
          ) : null}
        </View>

        <View style={[styles.topbarSide, styles.topbarSideRight]}>
          <Pressable onPress={() => setMenuOpen(true)} style={styles.topBtn}>
            <Text style={styles.topBtnText}>≡ Menu</Text>
          </Pressable>
        </View>
      </View>

      <View style={styles.body}>
        {isDesktop ? desktopPage : mobilePage}
      </View>

      {showBottomNavigator && total > 0 ? (
        <DetailResultsNavigator
          mode="bottom"
          currentIndex={safeIndex}
          total={total}
          onPrev={handlePrev}
          onResults={handleResults}
          onNext={handleNext}
        />
      ) : null}

      <PublicMenu
        visible={menuOpen}
        onClose={closeMenu}
        returnTo={profileReturnTo}
      />
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: "#ffffff",
  },

  body: {
    flex: 1,
    minHeight: 0,
  },

  scroll: {
    flex: 1,
  },

  scrollContent: {
    paddingTop: 12,
    paddingBottom: 20,
  },

  topbar: {
    paddingHorizontal: 12,
    paddingBottom: 8,
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#eef2f7",
    backgroundColor: "#ffffff",
    columnGap: 10,
  },

  topbarSide: {
    minWidth: 86,
    justifyContent: "center",
  },

  topbarSideRight: {
    alignItems: "flex-end",
  },

  topbarCenter: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    minHeight: 42,
  },

  topBtn: {
    minHeight: 34,
    paddingHorizontal: 12,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#e5e7eb",
    backgroundColor: "#ffffff",
    alignItems: "center",
    justifyContent: "center",
  },

  topBtnText: {
    fontSize: 12,
    fontWeight: "900",
    color: "#111827",
  },

  page: {
    width: "100%",
    maxWidth: 720,
    paddingHorizontal: 12,
    rowGap: 10,
    alignSelf: "center",
  },

  pageDesktop: {
    maxWidth: 1280,
    flexDirection: "row",
    alignItems: "stretch",
    columnGap: 16,
    paddingTop: 12,
  },

  colLeft: {
    width: 260,
    minHeight: 0,
  },

  colCenter: {
    flex: 1,
    maxWidth: 980,
    alignItems: "center",
    minHeight: 0,
  },

  colRight: {
    width: 340,
    minHeight: 0,
  },

  colRightFrame: {
    flex: 1,
    minHeight: 0,
    overflow: "hidden",
  },

  fullWidth: {
    width: "100%",
    alignSelf: "stretch",
  },

  mobileFeaturedWrap: {
    width: "100%",
    marginTop: 2,
  },

  featuredHeader: {
    flexDirection: "row",
    alignItems: "baseline",
    justifyContent: "space-between",
    marginBottom: 4,
  },

  featuredTitle: {
    fontSize: 12,
    fontWeight: "900",
    color: "#111827",
  },

  featuredHint: {
    fontSize: 11,
    fontWeight: "800",
    color: "#6b7280",
  },

});
